import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import "./user.css";
import { useDispatch, useSelector } from "react-redux";
import { fetchThemeData } from "../../admin/themeSlice";
import { useLocation, useSearchParams } from "react-router-dom";
import fallbackDesktopBg from "../../img/background.png";
import fallbackMobileBg from "../../img/mobile.jpg";

const FROM_MOBILE_APP_STORAGE_KEY = "lcp_fromMobileApp";

function resolveLogoUrl(rawLogoUrl) {
  const fallback = process.env.REACT_APP_EM_LOGO || "";
  const s3Source = (process.env.REACT_APP_S3_SOURCE || "").replace(/\/+$/, "");
  const incoming = String(rawLogoUrl || "").trim();

  if (!incoming) return fallback;

  if (incoming.startsWith("undefined/")) {
    return s3Source ? `${s3Source}/${incoming.replace(/^undefined\/+/, "")}` : fallback;
  }

  if (/^https?:\/\//i.test(incoming)) return incoming;

  if (s3Source) return `${s3Source}/${incoming.replace(/^\/+/, "")}`;

  return fallback;
}

function resolveThemeBackground(themeData, isMobile) {
  const bgKey = isMobile ? themeData?.background_mob : themeData?.background_desk;
  if (!bgKey) return null;
  return `${process.env.REACT_APP_S3_PATH || ""}${bgKey}`;
}

/** Routes where the header Back button is hidden when opened from the mobile app WebView. */
function isHideBackTargetPath(pathname) {
  const p = pathname.replace(/\/+$/, "") || "/";
  if (p === "/" || p === "/login") return true;
  return ["/game", "/leaderboard", "/feedback", "/thankyou", "/rules"].includes(p);
}

function USER({ children }) {
  const dispatch = useDispatch();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const headerRef = useRef(null);

  const { companyLogoUrl, backButtonUrl } = useSelector((state) => state.ui);
  const [logoUrl, setLogoUrl] = useState("");
  const [backUrl, setBackUrl] = useState("");
  const [blockLandscape, setBlockLandscape] = useState(false);

  const { status: themeStatus, data: themeData } = useSelector((state) => state.theme);

  const applyCustomTheme = (theme) => {
    if (!theme) return;
    document.documentElement.style.setProperty("--text-color", theme.text_color);
    document.documentElement.style.setProperty("--ui-color-1", theme.ui_color_1);
    document.documentElement.style.setProperty("--ui-color-2", theme.ui_color_2);
    document.documentElement.style.setProperty("--option-color", theme.option_color);
    document.documentElement.style.setProperty("--option-text-color", theme.option_text_color);
  };

  useEffect(() => {
    if (location.pathname === "/" || /^\/login(?:\/)?$/.test(location.pathname)) return;
    if (themeStatus === "idle") {
      dispatch(fetchThemeData({ themeId: null }));
    }
  }, [themeStatus, dispatch, location.pathname]);

  useEffect(() => {
    setLogoUrl(resolveLogoUrl(companyLogoUrl));
  }, [companyLogoUrl]);

  useEffect(() => {
    setBackUrl(backButtonUrl || "");
  }, [backButtonUrl]);

  const fromMobileAppInUrl = searchParams.get("fromMobileApp") === "true";
  useEffect(() => {
    if (!fromMobileAppInUrl) return;
    try {
      sessionStorage.setItem(FROM_MOBILE_APP_STORAGE_KEY, "true");
    } catch (_) {
      /* ignore */
    }
  }, [fromMobileAppInUrl]);

  /** True when URL has fromMobileApp=true or we persisted it (e.g. after client-side navigation). */
  const isFromMobileApp = useMemo(() => {
    let fromSession = false;
    try {
      fromSession = sessionStorage.getItem(FROM_MOBILE_APP_STORAGE_KEY) === "true";
    } catch (_) {
      fromSession = false;
    }
    return fromMobileAppInUrl || fromSession;
  }, [fromMobileAppInUrl, location.pathname]);

  const hideBackButton = useMemo(
    () => isFromMobileApp && isHideBackTargetPath(location.pathname),
    [isFromMobileApp, location.pathname]
  );

  useEffect(() => {
    if (!backUrl) return;

    const handleBrowserBack = () => {
      window.location.href = backUrl;
    };

    window.history.pushState({ lcpBackHandled: true }, "", window.location.href);
    window.addEventListener("popstate", handleBrowserBack);

    return () => {
      window.removeEventListener("popstate", handleBrowserBack);
    };
  }, [backUrl, location.pathname]);

  const isOverlayHeaderRoute = useMemo(() => {
    const p = (location.pathname || "").replace(/\/+$/, "") || "/";
    return (
      p === "/" ||
      p === "/login" ||
      p === "/game" ||
      /^\/game\/stage\/[^/]+$/i.test(p) ||
      p === "/leaderboard"
    );
  }, [location.pathname]);

  useEffect(() => {
    const updateLandscapeBlock = () => {
      if (typeof window === "undefined" || !window.matchMedia) {
        setBlockLandscape(false);
        return;
      }
      const isLandscape = window.matchMedia("(orientation: landscape)").matches;
      const isTouchDevice = window.matchMedia("(pointer: coarse)").matches;
      const isSmallScreen = window.matchMedia("(max-width: 1024px)").matches;
      setBlockLandscape(Boolean(isLandscape && isTouchDevice && isSmallScreen));
    };

    updateLandscapeBlock();
    window.addEventListener("resize", updateLandscapeBlock);
    window.addEventListener("orientationchange", updateLandscapeBlock);
    return () => {
      window.removeEventListener("resize", updateLandscapeBlock);
      window.removeEventListener("orientationchange", updateLandscapeBlock);
    };
  }, []);

  useEffect(() => {
    if (themeStatus === "succeeded" && themeData) {
      applyCustomTheme(themeData.colors);
      const body = document.body;
      if (!body.classList.contains("common-bg")) return;

      const isMobile = window.innerWidth <= 768;
      const fallbackBg = isMobile ? fallbackMobileBg : fallbackDesktopBg;
      const adminBgUrl = resolveThemeBackground(themeData, isMobile);

      if (!adminBgUrl) {
        body.style.backgroundImage = `url("${fallbackBg}")`;
        return;
      }

      const img = new Image();
      img.onload = () => {
        body.style.backgroundImage = `url("${adminBgUrl}")`;
      };
      img.onerror = () => {
        body.style.backgroundImage = `url("${fallbackBg}")`;
      };
      img.src = adminBgUrl;
    }
  }, [themeStatus, themeData]);

  useEffect(() => {
    const handleResize = () => {
      if (themeStatus !== "succeeded" || !themeData) return;
      const body = document.body;
      if (!body.classList.contains("common-bg")) return;

      const isMobile = window.innerWidth <= 768;
      const fallbackBg = isMobile ? fallbackMobileBg : fallbackDesktopBg;
      const adminBgUrl = resolveThemeBackground(themeData, isMobile);
      body.style.backgroundImage = `url("${adminBgUrl || fallbackBg}")`;
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [themeStatus, themeData]);

  const handleLogoClick = (e) => {
    if (!isFromMobileApp) return;
    e.preventDefault();
    window.GameStatus?.postMessage?.("navigate_back");
  };

  useLayoutEffect(() => {
    const applyHeaderHeight = () => {
      const h = headerRef.current?.offsetHeight || 0;
      document.documentElement.style.setProperty("--app-header-h", `${h}px`);
    };
    applyHeaderHeight();
    window.addEventListener("resize", applyHeaderHeight);
    return () => window.removeEventListener("resize", applyHeaderHeight);
  }, [logoUrl, hideBackButton, backUrl]);

  return (
    <div
      className={`app-shell${isOverlayHeaderRoute ? " app-shell--overlay-header" : ""}`}
    >
      <header className="upperaction" ref={headerRef}>
        <a
          href={process.env.REACT_APP_BASE_URL || "/"}
          className="header-logo-button"
          onClick={handleLogoClick}
          aria-label="Go to home"
        >
          <img
            src={logoUrl}
            className="logo-holder"
            alt="Company logo"
            onError={(e) => {
              const fallback = process.env.REACT_APP_EM_LOGO || "";
              if (e.currentTarget.src !== fallback) e.currentTarget.src = fallback;
            }}
          />
        </a>
        {!hideBackButton && backUrl ? (
          <div className="back-holder">
            <a href={backUrl} className="back-default">
              Back
            </a>
          </div>
        ) : null}
      </header>
      <div className="app-shell-content">{children}</div>
      {blockLandscape ? (
        <div className="global-rotate-overlay">
          Please rotate your device to portrait mode
        </div>
      ) : null}
    </div>
  );
}

export default USER;
